import { Link, useLocation } from 'react-router'
import { EntityAvatar } from '@/components/ui/entity-avatar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Dash } from './list/commonCells'
import type { ContactRow } from './query/types'

/** The name with the job title under it; the list's search, sort and page travel with the link as the way back. */
export function ContactNameCell({ row }: { row: ContactRow }) {
  const location = useLocation()
  return (
    <div className="flex max-w-[24rem] items-center gap-2">
      <EntityAvatar kind="person" name={row.full_name} size="md" />
      <div className="flex min-w-0 flex-col">
        <Link
          to={`/relaties/contacten/${row.person_id}`}
          state={{ from: location.search }}
          title={row.full_name}
          className="truncate font-medium underline-offset-2 hover:underline"
        >
          {row.full_name}
        </Link>
        {row.job_title ? <span className="truncate text-xs text-muted-foreground">{row.job_title}</span> : null}
      </div>
      {row.status === 'inactief' ? <span className="text-xs text-muted-foreground">(gearchiveerd)</span> : null}
    </div>
  )
}

/** The primary organisation as a link, the other current ones behind "+n" (design §4.2). */
export function ContactOrganisationCell({ row }: { row: ContactRow }) {
  const primary = row.primary_organisation
  if (!primary) return <Dash />
  const others = row.other_organisations
  // A table cell grows with its text, so a long name needs a width to be cut off at: the
  // other columns would be pushed off the screen.
  return (
    <span className="flex max-w-56 items-center gap-1">
      <Link to={`/relaties/organisaties/${primary.id}`} title={primary.name} className="truncate text-foreground underline-offset-2 hover:underline">
        {primary.name}
      </Link>
      {others.length > 0 ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label={`Nog ${others.length} ${others.length === 1 ? 'organisatie' : 'organisaties'}`}
              className="rounded px-1 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              +{others.length}
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="flex w-auto max-w-xs flex-col gap-1 p-2">
            {others.map((organisation) => (
              <Link key={organisation.id} to={`/relaties/organisaties/${organisation.id}`} className="text-sm break-words underline-offset-2 hover:underline">
                {organisation.name}
                {organisation.role ? <span className="text-muted-foreground"> · {organisation.role}</span> : null}
              </Link>
            ))}
          </PopoverContent>
        </Popover>
      ) : null}
    </span>
  )
}
